import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {GammaSockets} from "#material/nodes/color/gamma/definition"
import {gammaDefinition} from "#material/nodes/color/gamma/definition"
import {threeConvert, threeRGBColor, threeValue} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import type {CyclesReturns} from "#material/sockets/cycles"
import {cyclesColor} from "#material/cycles/helpers"
import type {ThreeReturns} from "#material/sockets/three"
import type {ColorNode, GammaNode} from "#material/cycles/types"
import {ThreeTsl as TSL} from "#material/three/exports"

@registerMaterialGraphNode
export class Gamma extends MaterialNode<GammaSockets> {
    public override get definition() {
        return gammaDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<GammaSockets>> => {
        if (this.parameters.ui?.disabled) {
            return {
                color:
                    (await evaluator.get(this.parameters.inputs.color))?.toColor() ??
                    threeConvert(this.parameters.settings.color, threeRGBColor) ??
                    threeRGBColor({
                        r: 0,
                        g: 0,
                        b: 0,
                    }),
            }
        }

        const {color, gamma} = await evaluator.getAll({color: this.parameters.inputs.color, gamma: this.parameters.inputs.gamma})

        const colorValue = color?.toColor() ?? threeConvert(this.parameters.settings.color, threeRGBColor) ?? threeRGBColor({r: 0, g: 0, b: 0})
        const gammaValue = gamma?.toFloat() ?? threeConvert(this.parameters.settings.gamma, threeValue) ?? threeValue(1)
        return {color: colorValue.rgb.pow(TSL.vec3(gammaValue)).toColor()}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<GammaSockets>> => {
        if (this.parameters.ui?.disabled) {
            const inputColor = await evaluator.get(this.parameters.inputs.color)
            if (inputColor) {
                return {color: inputColor}
            } else {
                const colorNode: ColorNode = {
                    type: "color",
                    inputs: {},
                    parameters: {value: cyclesColor(this.parameters.settings.color)},
                    resources: {},
                }
                return {color: [colorNode, "color"]}
            }
        }

        const gammaNode: GammaNode = {
            type: "gamma",
            inputs: await evaluator.getAll({color: this.parameters.inputs.color, gamma: this.parameters.inputs.gamma}),
            parameters: {color: cyclesColor(this.parameters.settings.color), gamma: this.parameters.settings.gamma},
            resources: {},
        }

        return {
            color: [gammaNode, "color"],
        }
    }
}
